<template>
  <div>
    <swiper
      ref="featureSwiper"
      :options="swiperOptionv"
      class="vertical-swiper-box"
      @slideChange="featureChanged"
    >
      <!-- bill intro -->
      <swiper-slide class="swiper-item">
        <div class="vertical-positioner">
          <!-- swipe -->
          <div class="swipe">
            <div class="left-slider-right d-flex flex-column justify-content-center align-items-center">
              <div class="slider mw-60 w-100 d-flex align-items-start">
                <div class="flip-box w-100">
                  <div
                    class="flip-box-inner"
                    :style="{ transform: 'rotateY(' + deg + 'deg)' }"
                  >
                    <div class="flip-box-front">
                      <img
                        ref="frontImage"
                        class="front-image"
                        :src="bill_image_front"
                      >
                    </div>
                    <div class="flip-box-back">
                      <img
                        class="front-image"
                        :src="bill_image_back"
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="feature-description d-flex flex-column">
              <div class="feature-text">
                <p>
                  <strong>{{ denomination }} Euro {{ $t("bills." + series) }}</strong> {{ $t("bills.home") }}
                </p>
              </div>
            </div>
            <div class="icon-flip d-flex flex-column align-items-center">
              <img src="@/assets/placeholders/flip.svg" @click="flipBill">
            </div>
          </div> <!-- /swipe -->
        </div>
      </swiper-slide>

      <swiper-slide
        v-for="(item, index) in features"
        :key="item.slug"
        class="swiper-item green"
      >
        <BillListItemFeature
          v-bind="item"
          :series="series"
          :denomination="denomination"
          :top="item.top"
          :right="item.right"
          :bottom="item.bottom"
          :left="item.left"
        />
      </swiper-slide>

      <div
        slot="button-prev"
        class="swiper-button-prev swiper-button-prev-v swiper-button-white"
      />
      <div
        slot="button-next"
        class="swiper-button-next swiper-button-next-v swiper-button-white"
      />
    </swiper>

    <!-- click -->
    <div class="click d-flex flex-column justify-content-between">
      <div class="top d-flex flex-column align-items-center previous-top">
        <a href="" title="previous feature">{{ $t("bills.features." + prevFeatureSlug) }}</a>
      </div>
      <div class="left-slider-right d-flex justify-content-between">
        <div class="d-flex align-items-start" />
        <div class="d-flex align-items-end" />
      </div>
      <div class="d-flex flex-column align-self-middle previous-bottom">
        <a href="" title="next feature">{{ $t("bills.features." + nextFeatureSlug) }}</a>
      </div>
    </div>
    <!-- /click -->
  </div>
</template>

<script>
import 'swiper/dist/css/swiper.css';
import { swiper, swiperSlide } from 'vue-awesome-swiper';

import BillListItemFeature from './BillListItemFeature.vue';

const featureImages = require.context('@/assets/features/', false);

// Methods don't work in data(), so it has to be outside that scope
function findSlideIndex(features, slug) {
  if (slug) {
    const index = features.findIndex(i => i.slug === slug);
    console.assert(index !== -1);

    // Offset + 1 as first slide is not a feature
    return index + 1;
  }

  // No feature was selected
  return 0;
}

export default {
  components: {
    swiper,
    swiperSlide,
    BillListItemFeature,
  },
  filters: {
    capitalize(value) {
      if (!value) return '';

      const valStr = value.toString();
      return valStr.charAt(0).toUpperCase() + valStr.slice(1);
    },
  },
  props: {
    denomination: {
      type: Number,
      required: true,
    },
    series: {
      type: String,
      required: true,
    },
    features: {
      type: Array,
      required: true,
    },
    featureSlug: {
      type: String,
      required: false,
      default: null,
    },
  },

  // Swiper stuff
  data() {
    const initialSlide = findSlideIndex(this.features, this.featureSlug);

    return {
      rotated: false,
      deg: 0,
      swiperOptionv: {
        resistanceRatio: 0.95,
        slidesPerView: 1,
        preventClicks: false,
        preventClicksPropagation: false,
        longSwipes: false,
        loop: true,
        direction: 'vertical',
        mousewheel: true,
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        navigation: {
          nextEl: '.swiper-button-next-v',
          prevEl: '.swiper-button-prev-v',
        },
        // For some reason, this.currentSlide doesn't work here (yet).
        initialSlide,
      },
    };
  },
  computed: {
    featureSwiper() {
      return this.$refs.featureSwiper.swiper;
    },
    renderSlides() {
      // Number of slides to render before and after current slide.
      const renderAround = 2;

      // - 1 due to first slide not being a feature
      return whichToRender(this.currentSlide - 1, this.features.length, renderAround);
    },
    currentSlide() {
      return findSlideIndex(this.features, this.featureSlug);
    },
    bill_image_front() {
      return featureImages(`./${this.series}-${this.denomination}_front.jpg`);
    },
    bill_image_back() {
      return featureImages(`./${this.series}-${this.denomination}_back.jpg`);
    },
    nextFeatureSlug() {
      const index = this.features.findIndex(i => i.slug === this.featureSlug) + 1;

      // We're at the last, next is home
      if (index > this.features.length - 1) return 'home';

      return this.features[index].slug;
    },
    prevFeatureSlug() {
      const index = this.features.findIndex(i => i.slug === this.featureSlug) - 1;

      switch (index) {
        case -2:
          // We're at home, prev is last
          return this.features[this.features.length - 1].slug;
        case -1:
          // We're at the first, prev is home
          return 'home';
        default:
          return this.features[index].slug;
      }
    },
  },
  watch: {
    currentSlide(index, oldIndex) {
      console.log('currentSlide (feature) changed from', oldIndex, 'to', index);

      // Make sure the swiper is in sync with the component
      if (index !== this.featureSwiper.realIndex) {
        this.featureSwiper.slideToLoop(index);
      }
    },
  },
  methods: {
    featureChanged() {
      // Tell our parent the feature has changed
      // Expecting them to update the slug correspondingly
      // Substract 1 for the first non-feature slide
      const index = this.featureSwiper.realIndex;

      // Only fire event if the index actually changed
      if (index !== this.currentSlide) {
        if (index > 0) {
          // Note that our first slide is not a feature, hence - 1
          const feature = this.features[index - 1];
          this.$emit('feature-slug', feature.slug);
        } else {
          // No current feature, emit empty
          this.$emit('feature-slug', null);
        }
      }
    },
    flipBill() {
      this.deg = this.rotated ? 0 : 180;
      this.rotated = !this.rotated;
    },
  },
  mounted() {
    findSlideIndex(this.features, this.featureSlug);
  }
};

</script>

<style scoped lang="scss">

  // General swiper stuff
  .swiper-wrapper {
    position: absolute;
    top:0;
    left:0;
    right:0;
    bottom:0;
    .swiper-box {
      width: 100%;
      height: 100vh;
    }
    .swiper-item {
      text-align: center;
    }

    .vertical-swiper-box {
      width: 100%;
      height: 100vh;
      .swiper-button-prev.swiper-button-prev-v {
        background-image: url("~@/assets/placeholders/arrow.svg");
        background-size: auto auto;
        background-position: center;
        transform: rotate(90deg) !important;
        left:50% !important;
        margin-left:-13px;
        top:0 !important;
        margin-top: 18px;
        opacity: 0.5;
        &:hover {
          opacity: 1;
        }
      }
      .swiper-button-next.swiper-button-next-v {
        background-image: url("~@/assets/placeholders/arrow.svg");
        background-size: auto auto;
        transform: rotate(-90deg) !important;
        background-position: center;
        left:50% !important;
        margin-left:-13px;
        top:100% !important;
        margin-top: -102px;
        opacity: 0.5;
        &:hover {
          opacity: 1;
        }
      }
    }
  }

  .swiper-button-next:focus, .swiper-button-prev:focus {
    border: 0;
    outline:0;
  }

  @media only screen and (max-width: 992px) {
    .swiper-button-next, .swiper-button-prev {
      display: none;
    }
  }

  // DNB swiper stuff
  .swipe {
    .left-slider-right {
      margin: 100px 0 16px 0;
      @media (max-width: 992px) {
        margin:32px 0 16px 0;
      }
      .slider {
        position: relative;
        padding: 10px;
        border: 1px solid #9994c2;
        max-width: 600px;
        .flip-box {
          background-color: transparent;
          perspective: 1000px;
          .flip-box-inner {
            position: relative;
            width: 100%;
            height: 100%;
            text-align: center;
            transition: transform 0.5s;
            transform-style: preserve-3d;
            display: flex;
            flex-flow: row nowrap;
            .flip-box-front, .flip-box-back {
              box-sizing: border-box;
              width: 100%;
              flex: none;
              backface-visibility: hidden;
              -webkit-backface-visibility: hidden;
            }
            .flip-box-back {
              transform: rotateY(180deg);
              margin-left: -100%;
            }
          }
        }
        img {
          width: 100%;
          height: auto;
          text-align: center;
;        }
        .frame {
          position: absolute;
          left: 78%;
          right: 6%;
          top: -1px;
          bottom:-1px;
          border: 3px solid #fff;
        }
      }
    }
    .feature-description {
      max-width: 600px;
      height: auto;
      margin: 0 auto;
      p {
        width: auto;
        text-align: left;
      }
      img {
        display: inline;
        max-width: 160px;
        max-height: 160px;
        margin: 4px 12px 0 0;
        @media (max-width: 575.98px) {
          max-width: 120px;
          max-height: 120px;
        }
      }
    }
    .icon-flip img{
      margin-top: 16px;
      width: 54px;
      height: 38px;
      opacity: 0.3;
      &:hover {
        opacity: 0.5;
      }
      @media (max-width: 575.98px) {
        width: 43px;
        height: 30px;
      }
    }
  }

  .click {
    height: calc(100vh - 56px);
    position: absolute;
    top: 0; left: 0; bottom: 0; right: 0;
    pointer-events: none;
    .previous-top {
      margin-top: 10px;
      @media (min-width: 992px) {
        margin-top: 60px;
      }
    }
    .previous-bottom {
      margin-bottom: 24px;
      @media (min-width: 992px) {
        margin-bottom: 48px;
      }
    }
    .previous-top, .previous-bottom {
      @media (max-width: 992px) {
        visibility: hidden;
      }
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/bill-list/BillListItem.vue